/** vendor */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

/** Lib */
import { useToast } from '../../hooks/useToast'
import { mergeListItems } from '../../utils/updateItemsInShoppinglist'

/** state */
import { 
    updateShoppinglist, 
    updateUserStore, 
    getUserStores, 
    getSharedWithMe 
} from '../../actions/user.actions'

const ManageFoodStoreItemsDialog = ({
    foodstore,
    storeItems, 
    canEditStore = false, 
    onAction = (action) => null 
}) => {

    const dispatch = useDispatch()
    const toast = useToast()

    const [itemsToManage, setItemsToManage] = useState([])
    const shoppingLists = useSelector(state => state.user_data.shoppinglists || [])
    const sharedWithMeShoppinglists = useSelector(state => state.user_data?.shared_with_me.filter(m => m.type === 'UserShoppinglist'))
    const [saving, setSaving] = useState(false)
    const [shoppingListOptions, setShoppingListOptions] = useState([])
    const [selectedMoveListId, setSelectedMoveListId] = useState('')

    const currentUsersEmail = useSelector(state => state.auth?.user?.user?.email)

    const removeFromStore = async () => {
        const storeItemsToRemove = [...itemsToManage].filter(i => i?.checked)
        
        const newStoreItems = foodstore?.items.filter(
            (storeItem) => !storeItemsToRemove.some(
                (removeItem) => 
                    storeItem?.value === removeItem?.value &&
                    storeItem?.date_added === removeItem?.date_added
            )
        )
        
        await dispatch(updateUserStore(foodstore?.id, { items: newStoreItems }))
    }

    const sendTolist = async () => {
        let storeItemsToMove = [...itemsToManage].filter(i => i?.checked)

        const listToUpdate = [...shoppingLists, ...sharedWithMeShoppinglists].find(
            l => l.user_shoppinglist_id === parseInt(selectedMoveListId)
        )

        const mergedItems = mergeListItems([...listToUpdate.ingredients], storeItemsToMove)

        try {
            await dispatch(updateShoppinglist(selectedMoveListId, { ingredients: mergedItems } ))        
        } catch (error) {
            console.error(error)
        }
    }

    const prepareItems = () => {
        const addEdit = [...storeItems].map((item) => {
            return {...item, ...{ checked: true }}
        })
        
        setItemsToManage(addEdit)
    }

    const calcExpDate = (dateStr, estimated_expiry_days = 14) => {
        const date = new Date(dateStr)
        date.setDate(date.getDate() + estimated_expiry_days)
    
        return date
    }
    
    const formatExpDate = (dateStr, estimated_expiry_days = 14) => {
        return calcExpDate(dateStr, estimated_expiry_days = 14).toDateString()
    }

    useEffect(() => {
        if (storeItems?.length > 0) {
            prepareItems()
        }
    }, [storeItems])

    useEffect(() => {
        if(shoppingLists && shoppingLists.length > 0 && currentUsersEmail) {
            const shoppingListsCopy = [...shoppingLists]
            const shoppingListOptions = shoppingListsCopy.map(i => { return { value: i.user_shoppinglist_id, name: i.name } })
            let editableSharedWithMeLists = []

            if(sharedWithMeShoppinglists && sharedWithMeShoppinglists.length > 0 && currentUsersEmail) {
                editableSharedWithMeLists = [...sharedWithMeShoppinglists].filter((item) => {
                    let canEdit = false

                    for(const user of item?.shared_with) {
                        if(user?.email === currentUsersEmail && user?.canEdit) {
                            canEdit = true
                            break
                        }
                    }

                    return canEdit
                }).map(i => { return { value: i.user_shoppinglist_id, name: i.name } })
            }
            const mergedOptions = [...shoppingListOptions, ...editableSharedWithMeLists]
            setShoppingListOptions(mergedOptions)
        }
    }, [shoppingLists, currentUsersEmail])

    const handleToggleItemChecked = (checked, index) => {
        const itemsToManageCopy = [...itemsToManage]
        itemsToManageCopy[index].checked = checked
        setItemsToManage(itemsToManageCopy)
    }

    const handleCancel = () => {
        onAction('cancel')
    }

    const handleMoveSelect = (e) => {
        setSelectedMoveListId(e.target.value)
    }

    const handleRemove = async () => {
        try {
            setSaving(true)

            if(selectedMoveListId) {
                await sendTolist()
            }

            await removeFromStore()
            onAction('remove')
            await dispatch(getSharedWithMe())
            await dispatch(getUserStores())

            setSaving(false)
            
            toast.open(selectedMoveListId ? 'Items Moved and store updated' : 'Store updated')
        } catch (error) {
            toast.open('Error Updating store, reload and try again.')
        }
    }

    return (
        <div className={`fixed z-50 inset-0 overflow-y-auto flex items-center justify-center px-2`}>
            <div className="fixed inset-0 bg-black opacity-60"></div>
            <div className="bg-an_white rounded shadow-md w-full md:max-w-xl mx-auto z-50">
                <h2 className="w-full text-xl text-an_white text-center items-center p-4 bg-an_green-dark rounded-t">
                    manage store items
                </h2>
                <div className="border-b border-black w-full justify-between items-center">
                    <ul className="w-full h-[60vh] overflow-y-auto">
                    {
                        itemsToManage?.length > 0 
                            ? itemsToManage?.map((item, index) => {
                                return (
                                    <li className="py-3 sm:py-4 px-4 odd:bg-an_white-dark hover:bg-an_white-darker1 min-w-full" key={index}>
                                        <div className="flex items-center space-x-4">
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-an_green-dark`}>
                                                    {item?.quantity || 1} {item?.unit || item?.default_unit || ''} {item.name}
                                                </p>

                                                <small className={`text-xs flex text-an_orange-darker1 to lowercase`}>
                                                    <span>{formatExpDate(item?.date_added, item?.estimated_expiry_days)} </span>
                                                </small>
                                            </div>

                                            {
                                                canEditStore &&
                                                <div className="inline-flex items-center text-base font-semibold text-an_green-dark">
                                                    <input 
                                                        className="w-4 h-4 text-an_green-dark bg-white border-black rounded"
                                                        type="checkbox" 
                                                        checked={item?.checked}
                                                        onChange={() => { handleToggleItemChecked(!item?.checked, index) }}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </li>              
                                )
                            }) : <div className="w-ful text-center text-base text-an_green-dark pt-40">nope.</div>
                    }
                    </ul>

                    <div className="pt-1 px-4 pb-2 bg-an_white-dark border-an_green-dark border-t">
                        <label className="block py-2 text-xs w-full">Send to Shopping List</label>
                        <select
                            value={selectedMoveListId}
                            disabled={saving} 
                            id="shoppinglistMove"
                            onChange={(e) => { handleMoveSelect(e)} }
                            className="border border-black text-an_green-dark text-sm rounded block w-full p-2"
                        >
                            <option value="">Select ...</option>
                            {
                                shoppingListOptions.map((option, i) => {
                                    return (
                                        <option key={i} value={option.value}>{option.name}</option>
                                    )
                                })
                            }
                        </select>
                    </div>
                </div>
            </div>

            <div className="flex justify-center w-screen px-2 pb-3 fixed bottom-0">
                <button
                    className="px-5 py-2 rounded w-36 text-an_green-dark bg-an_white mr-1"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    cancel
                </button>
                <button
                    disabled={itemsToManage?.length < 1 || saving}
                    onClick={handleRemove}
                    className={`${itemsToManage?.length < 1 || saving ? 'opacity-60' : ''} px-5 ml-1 py-2 relative rounded w-36 border border-an_orange-darker3 text-an_white bg-an_orange text-border-orange`}
                >
                    { selectedMoveListId ? 'move' : 'remove' }
                    {
                        saving &&
                            <div className="animate-pulse absolute right-2 top-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin ">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                            </div>  
                    }
                </button>
            </div>
        </div>
    )
}

export default ManageFoodStoreItemsDialog